import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";

/* ================= COMPONENT ================= */
const CustomerTable = ({
  customers,
  onEdit,
  onDelete,
  onView,
  searchTerm,
  onSearchChange,
  sortConfig,
  onSort,
  onPasswordChange,
}) => {
  const { t } = useTranslation();
  const [currentPage] = useState(1);
  const [pageSize] = useState(10);

  /* ================= DATA ================= */
  const sortedCustomers = useMemo(() => {
    if (!sortConfig.key) return customers;
    return [...customers].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [customers, sortConfig]);

  const filteredCustomers = useMemo(() => {
    return sortedCustomers.filter(
      (c) =>
        `${c.first_name} ${c.last_name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm),
    );
  }, [sortedCustomers, searchTerm]);

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCustomers.slice(start, start + pageSize);
  }, [filteredCustomers, currentPage, pageSize]);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const getStatusBadge = (status) => {
    const map = {
      active: "bg-success text-success-foreground",
      inactive: "bg-secondary text-secondary-foreground",
      pending: "bg-warning text-warning-foreground",
      blocked: "bg-error text-error-foreground",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          map[status] || map.active
        }`}
      >
        {t(status)}
      </span>
    );
  };

  /* ================= FIELDS (ONE SOURCE) ================= */
  const getCustomerFields = ({ customer, variant = "table" }) => {
    const isMobile = variant === "mobile";

    return {
      name: (
        <div className="font-medium">
          {customer.first_name} {customer.last_name}
        </div>
      ),

      email: <span>{customer.email}</span>,

      phone: <span>{customer.phone}</span>,

      province: <span>{customer.province?.name}</span>,

      orders: <span>{customer.order_count}</span>,

      status: getStatusBadge(customer.role.name),

      lastContact: (
        <span className="text-muted-foreground">
          {formatDate(customer.lastContact || new Date())}
        </span>
      ),

      actions: (
        <div className={`flex gap-2 ${isMobile ? "" : "justify-center"}`}>
          <button
            title={t("changePassword")}
            onClick={(e) => {
              e.stopPropagation();
              onPasswordChange(customer);
            }}
            className="text-amber-500"
          >
            <Icon name="Key" size={16} />
          </button>
          <button onClick={() => onView(customer)}>
            <Icon name="Eye" size={16} />
          </button>
          <button onClick={() => onEdit(customer)}>
            <Icon name="Edit" size={16} />
          </button>
          <button
            onClick={() => onDelete(customer)}
            className="text-error"
          >
            <Icon name="Trash2" size={16} />
          </button>
        </div>
      ),
    };
  };

  return (
    <div className="bg-white rounded-md p-4 border">
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {t("customerRecords")}
        </h3>
        <Input
          type="search"
          placeholder={t("searchCustomers")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64"
        />
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              {[
                { key: "name", label: t("customerName") },
                { key: "email", label: t("email") },
                { key: "phone", label: t("phone") },
                { key: "province", label: t("province") },
                { key: "orders", label: t("orderNumber") },
                { key: "status", label: t("status") },
                { key: "lastContact", label: t("lastContact") },
              ].map((c) => (
                <th
                  key={c.key}
                  onClick={() => onSort(c.key)}
                  className="p-3 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-1">
                    {c.label}
                    {sortConfig.key === c.key && (
                      <Icon
                        name={
                          sortConfig.direction === "asc"
                            ? "ChevronUp"
                            : "ChevronDown"
                        }
                        size={14}
                      />
                    )}
                  </div>
                </th>
              ))}
              <th className="p-3 text-center">{t("actions")}</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {paginatedCustomers.map((customer) => {
              const f = getCustomerFields({ customer });
              return (
                <tr
                  key={customer.id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => onView(customer)}
                >
                  <td className="p-3">{f.name}</td>
                  <td className="p-3">{f.email}</td>
                  <td className="p-3">{f.phone}</td>
                  <td className="p-3">{f?.province?.name}</td>
                  <td className="p-3">{f.orders}</td>
                  <td className="p-3">{f.status}</td>
                  <td className="p-3">{f.lastContact}</td>
                  <td
                    className="p-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {f.actions}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE CARDS ================= */}
      <div className="md:hidden space-y-4">
        {paginatedCustomers.map((customer) => {
          const f = getCustomerFields({
            customer,
            variant: "mobile",
          });

          return (
            <div
              key={customer.id}
              className="border rounded-lg p-4 space-y-3"
              onClick={() => onView(customer)}
            >
              <div className="flex justify-between items-center">
                {f.name}
                {f.status}
              </div>

              <div className="flex gap-2 text-sm">
                <Icon name="Mail" size={14} />
                {f.email}
              </div>

              <div className="flex gap-2 text-sm">
                <Icon name="Phone" size={14} />
                {f.phone}
              </div>

              <div className="flex gap-2 text-sm">
                <Icon name="Calendar" size={14} />
                {f.lastContact}
              </div>

              <div
                className="flex justify-end"
                onClick={(e) => e.stopPropagation()}
              >
                {f.actions}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerTable;
